/**
 * strategyParams — frontend mirror of the two DECLARATION-OWNED strategy-bar readers in
 * functions/utils/companyFeatures.js (readStoredFlipMinRoiPercent /
 * readStoredCoLivingMinBedroomsPostConversion).
 *
 * KEEP IN SYNC — the frontend can't import from functions/. A vm-eval sync guard in
 * functions/utils/_testCompanyFeaturesStrategyParams.js asserts FE/BE equivalence over a
 * shared case table; extend it if you change anything here.
 *
 * The rule these encode (see the backend header for the full story):
 *   1. buyBox.strategyParams is read FIRST, the legacy data.features location second.
 *   2. DECLARATION OWNERSHIP: once buyBox.strategies declares the strategy, its buy-box
 *      bar is authoritative — no fallback to the stale legacy features value. A user who
 *      declares "Fix & flip" and clears the ROI field means "use the system default";
 *      re-seeding the form from the legacy value would display the old bar and a later
 *      save would silently write it back.
 *   3. Same integer + bounds coercion as the backend, so the form never shows a value
 *      the underwriting resolvers would treat as unset.
 *
 * null = "no bar stored" (the system default applies; render the input blank).
 * minParkingSpaces is deliberately NOT mirrored: it's an MCP-owned analysis setting with
 * no declaration ownership, and the buy-box form never edits it.
 */

const coerceIntInBounds = (raw, min, max) => {
    if (!Number.isInteger(raw)) return null;
    if (raw < min || raw > max) return null;
    return raw;
};

const declares = (buyBox, strategy) =>
    Array.isArray(buyBox?.strategies) && buyBox.strategies.includes(strategy);

export function readStoredFlipMinRoiPercent(buyBox, features) {
    const fromBuyBox = coerceIntInBounds(buyBox?.strategyParams?.fix_and_flip?.minRoiPercent, 0, 1000);
    if (fromBuyBox != null) return fromBuyBox;
    if (declares(buyBox, 'fix_and_flip')) return null; // declared ⇒ buy box owns it
    return coerceIntInBounds((features || {}).flipMinRoiPercent, 0, 1000);
}

export function readStoredCoLivingMinBedroomsPostConversion(buyBox, features) {
    const fromBuyBox = coerceIntInBounds(buyBox?.strategyParams?.co_living?.minBedroomsPostConversion, 2, 20);
    if (fromBuyBox != null) return fromBuyBox;
    if (declares(buyBox, 'co_living')) return null; // declared ⇒ buy box owns it
    return coerceIntInBounds((features || {}).coLivingMinBedroomsPostConversion, 2, 20);
}
